import { Theme } from '@busybusy/webapp-react-ui';
import { useToastDispatch } from 'contexts/ToastContext';
import { usePermissions } from 'hooks';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { IPermissionCheckableMember } from 'utils/positionUtils';
import { useSchedulingAnalytics } from '.';
import { useSchedulingData } from '../context/SchedulingDataContext';
import { IScheduleReminderDetails, ISchedulingTimeOffEvent } from '../types/types';
import { isSchedulingEventTimeOff } from '../utils/utils';
import useCalendarView from './useCalendarView';
import useSchedulingReadablePopover from './useSchedulingReadablePopover';

function useSchedulingPopover(
  onEdit: (event: IScheduleReminderDetails) => void,
  onCopy: (event: IScheduleReminderDetails) => void,
  onEditTimeOff: (event: ISchedulingTimeOffEvent) => void,
  onCopyTimeOff: (event: ISchedulingTimeOffEvent) => void
) {
  const [t] = useTranslation();

  const { popoverEvent, popoverDetails, clearPopoverEvent, onEventClick } = useSchedulingReadablePopover();
  const analytics = useSchedulingAnalytics();
  const { reminderDetails } = useSchedulingData();
  const currentView = useCalendarView();
  const { hasPermissionToManage } = usePermissions();
  const toastDispatch = useToastDispatch();

  function onPopoverEventEdit() {
    if (!popoverEvent) {
      return;
    }

    // LMAO do something about this
    if (isSchedulingEventTimeOff(popoverEvent.event)) {
      onEditTimeOff(popoverEvent.event);
    } else {
      const item = findScheduleById(popoverEvent.event.id);
      if (item) {
        clearPopoverEvent();
        const anyUnmanageable = _.some(
          item.reminder.links,
          (link) => !hasPermissionToManage(link.member as unknown as IPermissionCheckableMember, 'manageSchedule')
        );
        if (anyUnmanageable) {
          toastDispatch({ type: 'OPEN', payload: { label: t('Schedule item is not editable.'), theme: Theme.DARK } });
        } else {
          if (item) {
            analytics.recordPopoverAction(currentView, 'EDIT_SCHEDULE');
            clearPopoverEvent();
            onEdit(item.reminder);
          }
        }
      }
    }
  }

  function findScheduleById(id: string): { reminder: IScheduleReminderDetails; index: number } | null {
    const index = reminderDetails.findIndex((schedule) => schedule.id === id);
    if (index !== -1) {
      return { reminder: reminderDetails[index], index };
    } else {
      return null;
    }
  }

  function onPopoverEventCopy() {
    // Need to open an event dialog with all the form data from the popover event but it not actually be the popover event.
    if (popoverEvent) {
      if (isSchedulingEventTimeOff(popoverEvent.event)) {
        onCopyTimeOff(popoverEvent.event);
      } else {
        const item = findScheduleById(popoverEvent.event.id);
        if (item) {
          analytics.recordPopoverAction(currentView, 'COPY_SCHEDULE');
          clearPopoverEvent();
          onCopy(item.reminder);
        }
      }
    }
  }

  return {
    onPopoverEventCopy,
    onPopoverEventEdit,
    clearPopoverEvent,
    onEventClick,
    popoverEvent,
    popoverDetails,
  };
}

export default useSchedulingPopover;
